import {
    type CSSProperties,
    useState
} from "react";
import {
    StyleSheet
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    DropdownMenu,
    ToggleGroup,
    DataTable,
    CheckBox,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    IDataTableExportPayload,
    IDataTableColumn
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DocsTable,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "DataTable";

const STANDALONE_ROWS = [
    "invoice",
    "receipt",
    "contract",
    "report"
] as const;

const getStandaloneStyle = (controlHeights: NCoreUIKit.ControlHeights, spaces: NCoreUIKit.Spaces): CSSProperties => ({
    paddingBottom: controlHeights.small + spaces.spacingSm * 2 + spaces.spacingLg * 2,
    flexDirection: "column",
    gap: spaces.spacingSm,
    position: "relative",
    display: "flex",
    width: "100%"
});

const STANDALONE_CODE = `<View style={{ paddingBottom: controlHeights.small + spaces.spacingSm * 2 + spaces.spacingLg * 2 }}>
    {rows}
</View>

<DataTable.SelectionBar
    selectedCount={selectedIds.length}
    onClose={() => setSelectedIds([])}
    actions={[
        {
            onPress: handleArchive,
            title: "Archive"
        }
    ]}
/>`;

const USAGE_CODE = `const columns: Array<IDataTableColumn<Invoice>> = [
    {
        accessor: (invoice) => invoice.customer,
        header: "Customer",
        isSortable: true,
        id: "customer"
    },
    {
        accessor: (invoice) => invoice.amount,
        header: "Amount",
        isSortable: true,
        isNumeric: true,
        id: "amount"
    }
];

<DataTable
    getRowId={(invoice) => invoice.id}
    caption="Invoices"
    columns={columns}
    data={invoices}
>
    <DataTable.Toolbar>
        <DataTable.Search />
    </DataTable.Toolbar>
    <DataTable.Content />
    <DataTable.Pagination />
</DataTable>`;

const COLUMNS_CODE = `const columns: Array<IDataTableColumn<Invoice>> = [
    {
        filterOptions: [
            {
                label: "Overdue",
                value: "overdue"
            }
        ],
        accessor: (invoice) => invoice.status,
        cell: (invoice) => <Badge title={invoice.status} />,
        header: "Status",
        cardRole: "status",
        isSortable: true,
        priority: 1,
        id: "status"
    },
    {
        cell: () => <DataTable.RowActions>
            <DropdownMenu.Item title="Edit" />
        </DataTable.RowActions>,
        header: "Actions",
        isHeaderHidden: true,
        width: "shrink",
        align: "end",
        id: "actions"
    }
];`;

const STICKY_CODE = `const stylesheet = StyleSheet.create({
    table: {
        maxHeight: 320
    }
});

<DataTable
    style={stylesheet.table}
    isHeaderSticky={true}
    caption="Invoices"
    columns={columns}
    data={invoices}
>
    <DataTable.Toolbar>
        <DataTable.Search />
    </DataTable.Toolbar>
    <DataTable.Content />
    <DataTable.Pagination />
</DataTable>`;

const SERVER_CODE = `<DataTable
    onSortingChange={(sorting) => loadInvoices({ sorting })}
    onPageChange={(page) => loadInvoices({ page })}
    isManualPagination={true}
    isManualFiltering={true}
    isManualSorting={true}
    totalItems={totalCount}
    isLoading={isFetching}
    data={invoices}
    page={page}
    ...
/>`;

const LAYOUTS = [
    {
        titleKey: "dataTablePage.layoutTable",
        value: "table"
    },
    {
        titleKey: "dataTablePage.layoutCards",
        value: "cards"
    }
] as const;

const DATA_STATES = [
    {
        titleKey: "dataTablePage.stateReady",
        value: "ready"
    },
    {
        titleKey: "dataTablePage.stateLoading",
        value: "loading"
    },
    {
        titleKey: "dataTablePage.stateEmpty",
        value: "empty"
    },
    {
        titleKey: "dataTablePage.stateError",
        value: "error"
    },
    {
        titleKey: "dataTablePage.stateDenied",
        value: "denied"
    }
] as const;

const PAGE_SIZE_OPTIONS = [
    2,
    3,
    6
];

const INITIAL_PAGE_SIZE = 3;

const INITIAL_SERVER_PAGE_SIZE = 2;

type RowType = {
    status: string;
    amount: number;
    name: string;
    id: string;
};

type LayoutType = (typeof LAYOUTS)[number]["value"];

type DataStateType = (typeof DATA_STATES)[number]["value"];

type ToggleItemType = {
    titleKey: string;
    value: string;
};

const getRows = (t: DocsTranslate): Array<RowType> => [
    {
        status: t("dataTablePage.statusPaid"),
        name: t("dataTablePage.customer1"),
        amount: 4820,
        id: "row1"
    },
    {
        status: t("dataTablePage.statusPending"),
        name: t("dataTablePage.customer2"),
        amount: 1260,
        id: "row2"
    },
    {
        status: t("dataTablePage.statusOverdue"),
        name: t("dataTablePage.customer3"),
        amount: 3150,
        id: "row3"
    },
    {
        status: t("dataTablePage.statusPaid"),
        name: t("dataTablePage.customer4"),
        amount: 980,
        id: "row4"
    },
    {
        status: t("dataTablePage.statusPending"),
        name: t("dataTablePage.customer5"),
        amount: 2740,
        id: "row5"
    },
    {
        status: t("dataTablePage.statusOverdue"),
        name: t("dataTablePage.customer6"),
        amount: 560,
        id: "row6"
    }
];

const getColumns = (t: DocsTranslate): Array<IDataTableColumn<RowType>> => [
    {
        header: t("dataTablePage.columnCustomer"),
        accessor: (row) => row.name,
        cardRole: "title",
        isSortable: true,
        id: "name"
    },
    {
        filterOptions: [
            t("dataTablePage.statusPaid"),
            t("dataTablePage.statusPending"),
            t("dataTablePage.statusOverdue")
        ].map((status) => ({
            value: status,
            label: status
        })),
        header: t("sample.columnStatus"),
        accessor: (row) => row.status,
        cardRole: "status",
        isSortable: true,
        id: "status"
    },
    {
        header: t("sample.columnAmount"),
        accessor: (row) => row.amount,
        cardRole: "amount",
        isSortable: true,
        isNumeric: true,
        id: "amount"
    }
];

const getActionsColumn = (t: DocsTranslate): IDataTableColumn<RowType> => ({
    cell: () => <DataTable.RowActions>
        <DropdownMenu.Item
            title={t("dataTablePage.actionView")}
        />
        <DropdownMenu.Item
            title={t("dataTablePage.actionEdit")}
        />
        <DropdownMenu.Separator />
        <DropdownMenu.Item
            title={t("dataTablePage.actionDelete")}
            type="danger"
        />
    </DataTable.RowActions>,
    header: t("dataTablePage.columnActions"),
    isHeaderHidden: true,
    width: "shrink",
    id: "actions",
    align: "end"
});

const WIDE_STYLE: CSSProperties = {
    width: "100%"
};

const SCROLL_STYLE: CSSProperties = {
    overflowX: "auto",
    maxWidth: "100%"
};

const ROW_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex",
    width: "100%"
};

const CARDS_STYLE: CSSProperties = {
    maxWidth: 380,
    width: "100%"
};

const stylesheet = StyleSheet.create({
    stickyTable: {
        maxHeight: 320
    }
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("dataTablePage.sortingTitle"),
        id: "sorting"
    },
    {
        title: t("dataTablePage.selectionTitle"),
        id: "selection"
    },
    {
        title: t("dataTablePage.standaloneTitle"),
        id: "standalone-bar"
    },
    {
        title: t("dataTablePage.columnsTitle"),
        id: "columns"
    },
    {
        title: t("dataTablePage.paginationTitle"),
        id: "pagination"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DataTablePage = () => {
    const {
        controlHeights,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        standaloneIds,
        setStandaloneIds
    ] = useState<Array<string>>([
        STANDALONE_ROWS[0],
        STANDALONE_ROWS[1]
    ]);

    const {
        t
    } = useDocsI18n();

    const [
        selectedRowIds,
        setSelectedRowIds
    ] = useState<Array<string>>([]);

    const [
        exportInfo,
        setExportInfo
    ] = useState("");

    const [
        layout,
        setLayout
    ] = useState<LayoutType>("table");

    const [
        page,
        setPage
    ] = useState(1);

    const [
        serverPageSize,
        setServerPageSize
    ] = useState(INITIAL_SERVER_PAGE_SIZE);

    const [
        dataState,
        setDataState
    ] = useState<DataStateType>("ready");

    const [
        deletedRowIds,
        setDeletedRowIds
    ] = useState<Array<string>>([]);

    const rows = getRows(t);

    const selectionRows = rows.filter((row) => !deletedRowIds.includes(row.id));

    const columns = getColumns(t);

    const selectionColumns = [
        ...columns.filter((column) => column.id !== "status"),
        getActionsColumn(t)
    ];

    const serverPageCount = Math.max(Math.ceil(rows.length / serverPageSize), 1);

    const serverPage = Math.min(page, serverPageCount);

    const serverRows = rows.slice((serverPage - 1) * serverPageSize, serverPage * serverPageSize);

    const code = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    const handleExport = (payload: IDataTableExportPayload) => {
        setExportInfo(t("dataTablePage.exportCaption", [
            payload.rows.length,
            payload.scope
        ]));
    };

    const handleLayoutChange = (value: string | Array<string>) => {
        const nextLayout = LAYOUTS.find((item) => item.value === value);

        if(nextLayout) {
            setLayout(nextLayout.value);
        }
    };

    const handleStateChange = (value: string | Array<string>) => {
        const nextState = DATA_STATES.find((item) => item.value === value);

        if(nextState) {
            setDataState(nextState.value);
        }
    };

    const renderToggleGroup = (title: string, items: ReadonlyArray<ToggleItemType>, value: string, onChange: (value: string | Array<string>) => void) => <div
        style={SCROLL_STYLE}
    >
        <ToggleGroup
            attachBehaviour="attached"
            onValueChange={onChange}
            isClearEnabled={false}
            variant="outline"
            title={title}
            value={value}
            size="small"
        >
            {
                items.map((item) => <ToggleGroup.Item
                    title={t(item.titleKey)}
                    value={item.value}
                    key={item.value}
                />)
            }
        </ToggleGroup>
    </div>;

    const renderDetail = (row: RowType) => <Text
        variant="bodyMediumSize"
        color="mid"
    >
        {t("dataTablePage.detailText", [
            row.name
        ])}
    </Text>;

    const getStateRows = () => dataState === "empty" || dataState === "error" ? [] : rows;

    return <Article>
        <ComponentHeader
            lead={t("dataTablePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("dataTablePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.overviewComposition")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.overviewCaption")}
            >
                <div
                    style={WIDE_STYLE}
                >
                    <DataTable
                        caption={t("dataTablePage.caption")}
                        initialPageSize={INITIAL_PAGE_SIZE}
                        getRowId={(row) => row.id}
                        columns={columns}
                        data={rows}
                    >
                        <DataTable.Toolbar>
                            <DataTable.Search
                                placeholder={t("sample.search")}
                            />
                        </DataTable.Toolbar>
                        <DataTable.Content />
                        <DataTable.Pagination
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                        />
                    </DataTable>
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("dataTablePage.sortingTitle")}
            id="sorting"
        >
            <Paragraph>
                {t("dataTablePage.sortingText")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.filterText")}
            </Paragraph>
            <Preview
                caption={exportInfo || t("dataTablePage.sortingCaption")}
            >
                <div
                    style={WIDE_STYLE}
                >
                    <DataTable
                        caption={t("dataTablePage.caption")}
                        getRowId={(row) => row.id}
                        sortBehaviour="multiple"
                        columns={columns}
                        data={rows}
                    >
                        <DataTable.Toolbar>
                            <DataTable.Search
                                placeholder={t("sample.search")}
                            />
                            <DataTable.FacetedFilter
                                columnId="status"
                            />
                            <DataTable.Spacer />
                            <DataTable.Export
                                onExport={handleExport}
                                fileName="invoices"
                            />
                        </DataTable.Toolbar>
                        <DataTable.FilterChips />
                        <DataTable.Content />
                    </DataTable>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("dataTablePage.selectionTitle")}
            id="selection"
        >
            <Paragraph>
                {t("dataTablePage.selectionText")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.expandText")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.selectionCaption", [
                    selectedRowIds.length
                ])}
            >
                <div
                    style={WIDE_STYLE}
                >
                    <DataTable
                        onSelectionDelete={(ids) => setDeletedRowIds((current) => [
                            ...current,
                            ...ids
                        ])}
                        onSelectionChange={setSelectedRowIds}
                        caption={t("dataTablePage.caption")}
                        renderExpandedRow={renderDetail}
                        selectedRowIds={selectedRowIds}
                        getRowLabel={(row) => row.name}
                        selectionBehaviour="multiple"
                        getRowId={(row) => row.id}
                        columns={selectionColumns}
                        layoutBehaviour="table"
                        data={selectionRows}
                    >
                        <DataTable.Toolbar>
                            <DataTable.Search
                                placeholder={t("sample.search")}
                            />
                        </DataTable.Toolbar>
                        <DataTable.SelectionBar />
                        <DataTable.Content />
                    </DataTable>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("dataTablePage.standaloneTitle")}
            id="standalone-bar"
        >
            <Paragraph>
                {t("dataTablePage.standaloneText")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.standaloneInsetText")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.standaloneCaption")}
            >
                <div
                    style={getStandaloneStyle(controlHeights, spaces)}
                >
                    {
                        STANDALONE_ROWS.map((id) => <CheckBox
                            onPress={() => setStandaloneIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [
                                ...current,
                                id
                            ])}
                            title={t(`dataTablePage.standaloneRow.${id}`)}
                            isChecked={standaloneIds.includes(id)}
                            key={id}
                        />)
                    }
                    <DataTable.SelectionBar
                        actions={[
                            {
                                title: t("dataTablePage.standaloneArchive"),
                                onPress: () => setStandaloneIds([])
                            }
                        ]}
                        onClose={() => setStandaloneIds([])}
                        selectedCount={standaloneIds.length}
                    />
                </div>
            </Preview>
            <CodeBlock
                code={STANDALONE_CODE}
                language="tsx"
            />
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.standalonePropSelectedCount"),
                        name: "selectedCount",
                        type: "number"
                    },
                    {
                        description: t("dataTablePage.standalonePropIsVisible"),
                        defaultValue: "selectedCount > 0",
                        name: "isVisible",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.standalonePropActions"),
                        type: "Array<DataTableSelectionBarActionType>",
                        name: "actions"
                    },
                    {
                        description: t("dataTablePage.standalonePropOnClose"),
                        type: "() => void",
                        name: "onClose"
                    },
                    {
                        description: t("dataTablePage.standalonePropBottomOffset"),
                        defaultValue: "spaces.spacingLg",
                        name: "bottomOffset",
                        type: "number"
                    },
                    {
                        description: t("dataTablePage.standalonePropLabel"),
                        defaultValue: "\"Selected records\" (localised)",
                        name: "accessibilityLabel",
                        type: "string"
                    }
                ]}
            />
        </Section>
        <Section
            title={t("dataTablePage.columnsTitle")}
            id="columns"
        >
            <Paragraph>
                {t("dataTablePage.columnsText")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.layoutText")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.columnsCaption")}
            >
                {renderToggleGroup(t("dataTablePage.layoutLabel"), LAYOUTS, layout, handleLayoutChange)}
                <div
                    style={ROW_STYLE}
                >
                    <div
                        style={layout === "cards" ? CARDS_STYLE : WIDE_STYLE}
                    >
                        <DataTable
                            caption={t("dataTablePage.caption")}
                            initialPageSize={INITIAL_PAGE_SIZE}
                            getRowId={(row) => row.id}
                            layoutBehaviour={layout}
                            columns={columns}
                            data={rows}
                        >
                            <DataTable.Toolbar>
                                <DataTable.Search
                                    placeholder={t("sample.search")}
                                />
                                <DataTable.FilterSheet />
                                <DataTable.Spacer />
                                <DataTable.ViewOptions />
                                <DataTable.DensityToggle />
                            </DataTable.Toolbar>
                            <DataTable.Content />
                            <DataTable.Pagination
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                            />
                        </DataTable>
                    </div>
                </div>
            </Preview>
            <CodeBlock
                code={COLUMNS_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("dataTablePage.stickyText")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.stickyCaption")}
            >
                <div
                    style={WIDE_STYLE}
                >
                    <DataTable
                        caption={t("dataTablePage.caption")}
                        style={stylesheet.stickyTable}
                        getRowId={(row) => row.id}
                        layoutBehaviour="table"
                        isHeaderSticky={true}
                        initialPageSize={6}
                        columns={columns}
                        data={rows}
                    >
                        <DataTable.Toolbar>
                            <DataTable.Search
                                placeholder={t("sample.search")}
                            />
                        </DataTable.Toolbar>
                        <DataTable.Content />
                        <DataTable.Pagination
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                        />
                    </DataTable>
                </div>
            </Preview>
            <CodeBlock
                code={STICKY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dataTablePage.paginationTitle")}
            id="pagination"
        >
            <Paragraph>
                {t("dataTablePage.paginationText")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.serverText")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.paginationCaption", [
                    serverPage,
                    serverPageCount
                ])}
            >
                <div
                    style={WIDE_STYLE}
                >
                    <DataTable
                        onPageSizeChange={setServerPageSize}
                        caption={t("dataTablePage.caption")}
                        getRowId={(row) => row.id}
                        pageSize={serverPageSize}
                        isManualPagination={true}
                        totalItems={rows.length}
                        layoutBehaviour="table"
                        onPageChange={setPage}
                        sortBehaviour="none"
                        columns={columns}
                        data={serverRows}
                        page={serverPage}
                    >
                        <DataTable.Content />
                        <DataTable.Pagination
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                        />
                    </DataTable>
                </div>
            </Preview>
            <CodeBlock
                code={SERVER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("dataTablePage.statesText")}
            </Paragraph>
            <Preview
                caption={t("dataTablePage.statesCaption")}
            >
                {renderToggleGroup(t("section.states"), DATA_STATES, dataState, handleStateChange)}
                <div
                    style={WIDE_STYLE}
                >
                    <DataTable
                        error={dataState === "error" ? t("dataTablePage.errorText") : null}
                        isPermissionDenied={dataState === "denied"}
                        onRetry={() => setDataState("ready")}
                        caption={t("dataTablePage.caption")}
                        isLoading={dataState === "loading"}
                        getRowId={(row) => row.id}
                        layoutBehaviour="table"
                        data={getStateRows()}
                        columns={columns}
                    >
                        <DataTable.Toolbar>
                            <DataTable.Search
                                placeholder={t("sample.search")}
                            />
                            <DataTable.Spacer />
                            <DataTable.Export />
                        </DataTable.Toolbar>
                        <DataTable.Content />
                    </DataTable>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("dataTablePage.a11yTable"),
                    t("dataTablePage.a11ySort"),
                    t("dataTablePage.a11yAnnounce"),
                    t("dataTablePage.a11ySelection"),
                    t("dataTablePage.a11yExpand"),
                    t("dataTablePage.a11yResize"),
                    t("dataTablePage.a11yCards"),
                    t("dataTablePage.a11yStates"),
                    t("dataTablePage.a11yToolbar"),
                    t("dataTablePage.a11yContext"),
                    t("dataTablePage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("dataTablePage.groupData")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.propData"),
                        type: "Array<T>",
                        name: "data"
                    },
                    {
                        description: t("dataTablePage.propColumns"),
                        type: "Array<IDataTableColumn<T>>",
                        name: "columns"
                    },
                    {
                        description: t("dataTablePage.propCaption"),
                        name: "caption",
                        type: "string"
                    },
                    {
                        description: t("dataTablePage.propGetRowId"),
                        type: "(row: T, index: number) => string",
                        name: "getRowId"
                    },
                    {
                        description: t("dataTablePage.propGetRowLabel"),
                        type: "(row: T, index: number) => string",
                        name: "getRowLabel"
                    },
                    {
                        description: t("dataTablePage.propLayoutBehaviour"),
                        type: "\"auto\" | \"table\" | \"cards\"",
                        defaultValue: "\"auto\"",
                        name: "layoutBehaviour"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("dataTablePage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("dataTablePage.propOnSizeChange"),
                        type: "(size: TableSize) => void",
                        name: "onSizeChange"
                    },
                    {
                        description: t("dataTablePage.propIsHeaderSticky"),
                        name: "isHeaderSticky",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.propIsFirstColumnSticky"),
                        name: "isFirstColumnSticky",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.propOnRowPress"),
                        type: "(rowId: string) => void",
                        name: "onRowPress"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("dataTablePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("dataTablePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("dataTablePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph>
                {t("dataTablePage.groupSorting")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.propSortBehaviour"),
                        type: "\"none\" | \"single\" | \"multiple\"",
                        defaultValue: "\"single\"",
                        name: "sortBehaviour"
                    },
                    {
                        type: "Array<{ id: string; direction: \"asc\" | \"desc\" }>",
                        description: t("dataTablePage.propSorting"),
                        name: "sorting"
                    },
                    {
                        type: "Array<{ id: string; direction: \"asc\" | \"desc\" }>",
                        description: t("dataTablePage.propInitialSorting"),
                        name: "initialSorting"
                    },
                    {
                        type: "(sorting: Array<IDataTableSortRule>) => void",
                        description: t("dataTablePage.propOnSortingChange"),
                        name: "onSortingChange"
                    },
                    {
                        description: t("dataTablePage.propIsManualSorting"),
                        name: "isManualSorting",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.propGlobalFilter"),
                        name: "globalFilter",
                        type: "string"
                    },
                    {
                        description: t("dataTablePage.propOnGlobalFilterChange"),
                        type: "(value: string) => void",
                        name: "onGlobalFilterChange"
                    },
                    {
                        description: t("dataTablePage.propColumnFilters"),
                        type: "Record<string, Array<string>>",
                        name: "columnFilters"
                    },
                    {
                        description: t("dataTablePage.propOnColumnFiltersChange"),
                        type: "(columnFilters: DataTableColumnFilters) => void",
                        name: "onColumnFiltersChange"
                    },
                    {
                        description: t("dataTablePage.propIsManualFiltering"),
                        name: "isManualFiltering",
                        defaultValue: "false",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                {t("dataTablePage.groupPagination")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.propPage"),
                        type: "number",
                        name: "page"
                    },
                    {
                        description: t("dataTablePage.propOnPageChange"),
                        type: "(page: number) => void",
                        name: "onPageChange"
                    },
                    {
                        description: t("dataTablePage.propPageSize"),
                        name: "pageSize",
                        type: "number"
                    },
                    {
                        description: t("dataTablePage.propInitialPageSize"),
                        name: "initialPageSize",
                        defaultValue: "20",
                        type: "number"
                    },
                    {
                        description: t("dataTablePage.propOnPageSizeChange"),
                        type: "(pageSize: number) => void",
                        name: "onPageSizeChange"
                    },
                    {
                        description: t("dataTablePage.propIsManualPagination"),
                        name: "isManualPagination",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.propTotalItems"),
                        name: "totalItems",
                        type: "number"
                    }
                ]}
            />
            <Paragraph>
                {t("dataTablePage.groupSelection")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.propSelectionBehaviour"),
                        type: "\"none\" | \"single\" | \"multiple\"",
                        name: "selectionBehaviour",
                        defaultValue: "\"none\""
                    },
                    {
                        description: t("dataTablePage.propSelectedRowIds"),
                        name: "selectedRowIds",
                        type: "Array<string>"
                    },
                    {
                        description: t("dataTablePage.propOnSelectionChange"),
                        type: "(selectedRowIds: Array<string>) => void",
                        name: "onSelectionChange"
                    },
                    {
                        description: t("dataTablePage.propOnSelectionDelete"),
                        type: "(selectedRowIds: Array<string>) => void",
                        name: "onSelectionDelete"
                    },
                    {
                        description: t("dataTablePage.propRenderExpandedRow"),
                        type: "(row: T, index: number) => ReactNode",
                        name: "renderExpandedRow"
                    },
                    {
                        description: t("dataTablePage.propExpandBehaviour"),
                        type: "\"none\" | \"single\" | \"multiple\"",
                        defaultValue: "\"single\"",
                        name: "expandBehaviour"
                    },
                    {
                        description: t("dataTablePage.propIsRowExpandable"),
                        type: "(row: T) => boolean",
                        name: "isRowExpandable"
                    },
                    {
                        description: t("dataTablePage.propExpandedRowIds"),
                        name: "expandedRowIds",
                        type: "Array<string>"
                    },
                    {
                        description: t("dataTablePage.propOnExpandedRowIdsChange"),
                        type: "(expandedRowIds: Array<string>) => void",
                        name: "onExpandedRowIdsChange"
                    }
                ]}
            />
            <Paragraph>
                {t("dataTablePage.groupColumns")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.propColumnVisibility"),
                        type: "Record<string, boolean>",
                        name: "columnVisibility"
                    },
                    {
                        type: "(columnVisibility: DataTableColumnVisibility) => void",
                        description: t("dataTablePage.propOnColumnVisibilityChange"),
                        name: "onColumnVisibilityChange"
                    },
                    {
                        description: t("dataTablePage.propColumnWidths"),
                        type: "Record<string, number>",
                        name: "columnWidths"
                    },
                    {
                        description: t("dataTablePage.propOnColumnWidthsChange"),
                        type: "(columnWidths: DataTableColumnWidths) => void",
                        name: "onColumnWidthsChange"
                    }
                ]}
            />
            <Paragraph>
                {t("dataTablePage.groupStates")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.propError"),
                        type: "string | null",
                        defaultValue: "null",
                        name: "error"
                    },
                    {
                        description: t("dataTablePage.propOnRetry"),
                        type: "() => void",
                        name: "onRetry"
                    },
                    {
                        description: t("dataTablePage.propRenderEmpty"),
                        type: "() => ReactNode",
                        name: "renderEmpty"
                    },
                    {
                        description: t("dataTablePage.propIsPermissionDenied"),
                        name: "isPermissionDenied",
                        defaultValue: "false",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dataTablePage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("dataTablePage.columnDefinitionTitle")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dataTablePage.colId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("dataTablePage.colHeader"),
                        type: "string",
                        name: "header"
                    },
                    {
                        type: "(row: T) => string | number | boolean | Date | null | undefined",
                        description: t("dataTablePage.colAccessor"),
                        name: "accessor"
                    },
                    {
                        type: "(row: T, index: number) => ReactNode",
                        description: t("dataTablePage.colCell"),
                        name: "cell"
                    },
                    {
                        description: t("dataTablePage.colIsSortable"),
                        defaultValue: "false",
                        name: "isSortable",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.colSortFirst"),
                        type: "\"asc\" | \"desc\"",
                        defaultValue: "\"asc\"",
                        name: "sortFirst"
                    },
                    {
                        type: "Array<{ label: string; value: string; count?: number }>",
                        description: t("dataTablePage.colFilterOptions"),
                        name: "filterOptions"
                    },
                    {
                        description: t("dataTablePage.colIsNumeric"),
                        defaultValue: "false",
                        name: "isNumeric",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.colAlign"),
                        type: "\"start\" | \"center\" | \"end\"",
                        name: "align"
                    },
                    {
                        description: t("dataTablePage.colWidth"),
                        type: "number | \"shrink\"",
                        name: "width"
                    },
                    {
                        description: t("dataTablePage.colMinWidth"),
                        name: "minWidth",
                        type: "number"
                    },
                    {
                        description: t("dataTablePage.colIsResizable"),
                        defaultValue: "true",
                        name: "isResizable",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.colIsHideable"),
                        defaultValue: "true",
                        name: "isHideable",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.colIsHeaderHidden"),
                        name: "isHeaderHidden",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dataTablePage.colPriority"),
                        defaultValue: "1",
                        name: "priority",
                        type: "number"
                    },
                    {
                        type: "\"title\" | \"subtitle\" | \"status\" | \"amount\" | \"field\" | \"action\" | \"hidden\"",
                        description: t("dataTablePage.colCardRole"),
                        defaultValue: "\"field\"",
                        name: "cardRole"
                    }
                ]}
            />
            <Paragraph>
                {t("dataTablePage.partsTitle")}
            </Paragraph>
            <DocsTable
                rows={[
                    [
                        code("DataTable.Toolbar"),
                        code("customTheme, style"),
                        t("dataTablePage.partToolbar")
                    ],
                    [
                        code("DataTable.Search"),
                        code("placeholder, accessibilityLabel"),
                        t("dataTablePage.partSearch")
                    ],
                    [
                        code("DataTable.FacetedFilter"),
                        code("columnId, title, side, align"),
                        t("dataTablePage.partFacetedFilter")
                    ],
                    [
                        code("DataTable.FilterChips"),
                        code("title"),
                        t("dataTablePage.partFilterChips")
                    ],
                    [
                        code("DataTable.FilterSheet"),
                        code("title"),
                        t("dataTablePage.partFilterSheet")
                    ],
                    [
                        code("DataTable.ViewOptions"),
                        code("title, side, align"),
                        t("dataTablePage.partViewOptions")
                    ],
                    [
                        code("DataTable.DensityToggle"),
                        code("customTheme, style"),
                        t("dataTablePage.partDensityToggle")
                    ],
                    [
                        code("DataTable.Export"),
                        code("fileName = \"export\", scope = \"auto\", variant = \"outline\", title, onExport"),
                        t("dataTablePage.partExport")
                    ],
                    [
                        code("DataTable.SelectionBar"),
                        code("isExportHidden = false, children, selectedCount, isVisible, actions, onClose, bottomOffset"),
                        t("dataTablePage.partSelectionBar")
                    ],
                    [
                        code("DataTable.Pagination"),
                        code("pageSizeOptions = [10, 20, 50, 100], unitTitle"),
                        t("dataTablePage.partPagination")
                    ],
                    [
                        code("DataTable.RowActions"),
                        code("children, title, side, align"),
                        t("dataTablePage.partRowActions")
                    ],
                    [
                        code("DataTable.Spacer"),
                        code("style"),
                        t("dataTablePage.partSpacer")
                    ],
                    [
                        code("DataTable.Content"),
                        code("customTheme, style"),
                        t("dataTablePage.partContent")
                    ]
                ]}
                headers={[
                    t("dataTablePage.partsHeaderName"),
                    t("dataTablePage.partsHeaderProps"),
                    t("props.description")
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dataTablePage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DataTablePage;
